/**
 * Service: cart
 * It maintains the state of the shopping cart
 * items - cart items of type event, inventory, membership, donation
 * id - cart id
 * couponCodes - coupons applied to the cart
 * membershipIds - membership ids of the transacting member (can be more than one - base + aux)
 * membershipCategoryId - membership level id of new membership being purchased.
 */

import { ApiService } from '@b2b/common/services/api/types';
import { AutorenewInCart, createAutorenewInCart } from '@b2b/types/cart';
import { TODO } from '@b2b/types/core/utils';
import { OrderContact } from '@b2b/types/order';
import { Organization } from '@b2b/types/organization';
import angular, { IFilterService, IQService } from 'angular';
import { BehaviorSubject, combineLatest, first, firstValueFrom, map } from 'rxjs';
import type { CartCouponsService } from './coupons/coupons.service';
import { CartCouponsServiceModule } from './coupons/coupons.service';
import type { CartEventsService } from './events/events.service';
import { CartEventsServiceModule } from './events/events.service';
import { CartMembershipsService, CartMembershipsServiceModule } from './memberships/memberships.service';

type ThemesHelperService = TODO;
type UserPreferencesService = TODO;
type CalendarService = TODO;
type HelpersService = TODO;

type CartStatus = 'idle' | 'loading';
interface CartState {
  status: CartStatus;
  autorenewInCart: AutorenewInCart;
}

function createInitialState(): CartState {
  return {
    status: 'idle',
    autorenewInCart: createAutorenewInCart(),
  };
}

export class CartService {
  static $inject = [
    '$q',
    '$filter',
    'ApiService',
    'HelpersService',
    'CartCouponsService',
    'CartMembershipsService',
    'CartEventsService',
    'ThemesHelperService',
    'UserPreferencesService',
    'CalendarService',
  ];
  private stateSubject = new BehaviorSubject<CartState>(createInitialState());
  state$ = combineLatest({
    status: this.stateSubject.pipe(map((state) => state.status)),
    autorenewInCart: this.stateSubject.pipe(map((state) => state.autorenewInCart)),
    memberships: this.memberships.state$,
  });
  model = {
    items: [],
    id: null,
    couponCodes: null,
    membershipIds: null,
    membershipCategoryId: null,
    verifyEntitlements: true,
  } as {
    items: any[];
    id: any | null;
    couponCodes: any | null;
    membershipIds: any | null;
    membershipCategoryId: any | null;
    verifyEntitlements: boolean;
    orderContact?: OrderContact | null;
    reservationId?: any;
  };
  itemCount = 0;
  initted = false;
  orderContact: Partial<OrderContact> | null = {};
  organization: Partial<Organization> = {};
  transactingMember: Partial<{
    member: any;
    membership: any;
    memberships: any;
    membershipCategoryId: any;
  }> | null = null;

  CODES = {
    errors: {
      SHOPPING_CART_DNE: 1033, // Shopping cart was not found
    },
  };

  constructor(
    private $q: IQService,
    private $filter: IFilterService,
    private ApiService: ApiService,
    private HelpersService: HelpersService,
    public coupons: CartCouponsService,
    public memberships: CartMembershipsService,
    public events: CartEventsService,
    private ThemesHelperService: ThemesHelperService,
    private UserPreferencesService: UserPreferencesService,
    private CalendarService: CalendarService
  ) {}

  init() {
    if (!this.initted) {
      this.memberships = this.memberships.init(this);
      this.coupons = this.coupons.init(this);
      this.events = this.events.init(this);
      this.getCart();
    }
    return this;
  }

  getSummary(options = {}) {
    return {
      data: this.memberships.getModel().concat(this.events.getEvents()),
      options: angular.extend(
        {
          editable: true,
          removable: true,
        },
        options
      ),
    };
  }

  /*
   * TODO: When we implement event tickets for organisations using the cart,
   * we need to enhance this to also check for organisation order contact
   */
  getOrderContact() {
    return this.model.orderContact?.customerId ? this.model.orderContact : null;
  }

  getOrderContactFullName() {
    const orderContact = this.model.orderContact;

    if (!orderContact) {
      return '';
    }

    return [orderContact.prefix, orderContact.firstName, orderContact.middleName, orderContact.lastName]
      .filter((name) => !!name)
      .join(' ');
  }

  getOrderContactType() {
    return 'Member';
  }

  /**
   * Prepares a valid payload to update the cart from our internal model
   */
  getSaveModel() {
    let model = angular.copy(this.model);
    const membershipIds = this.getTransactingMembershipId();

    angular.forEach(model.items, (v, k) => {
      v.couponCodes = null;
      delete v.couponCodes;
    });

    this.setTransactingMembershipCategory();
    model.membershipCategoryId = this.getTransactingMembershipCategory();
    model.membershipIds = membershipIds ? [membershipIds] : [];

    model = this.clearCouponsWhenAllItemsAreRemoved(model);

    return model;
  }

  /**
   * In B2B checkout when removing all items from a cart that had previously had a coupon applied,
   * it’s still living there in the background such that when you add another item to the cart,
   * the discounts are automatically applied but there is nowhere in the UI that shows the coupon.
   *
   * @link https://acmeticketing.atlassian.net/browse/ENG-13550
   *
   * @return {object} cart model
   */
  clearCouponsWhenAllItemsAreRemoved(cartModel) {
    if (cartModel.items.length === 0) {
      cartModel.couponCodes = [];
    }

    return cartModel;
  }

  getModel() {
    return this.model;
  }

  setModel(cart) {
    const reservationId = this.model.reservationId;

    this.model = cart;

    if (this.model) {
      if (this.model.id) {
        if (!reservationId) {
          //the first time we initialize our events, we also create a reservation id for the cart
          this.events.getReservation().then(
            (response: any) => {
              this.events.setReservation(response.payload);
            },
            () => {
              this.events.setReservation();
            }
          );
        } else {
          this.setReservation(reservationId);
        }
      }

      if (this.model.couponCodes instanceof Array) {
        this.coupons.setModel(this.model.couponCodes);
      } else {
        this.coupons.resetModel();
      }
      if (this.model.items instanceof Array) {
        const memberships = this.$filter('filter')(this.model.items, (item) => {
          return item.itemType.toLowerCase().indexOf('membership') > -1;
        });
        this.memberships.setModel(memberships);

        const events = this.$filter('filter')(this.model.items, (item) => {
          return (
            item.itemType.toLowerCase().indexOf('event') > -1 || item.itemType.toLowerCase().indexOf('inventory') > -1
          );
        });
        this.events.setModel(events);

        this.itemCount = this.model.items.reduce((count, item) => {
          let quantity = parseInt(item.quantity, 10);
          if (isNaN(quantity)) {
            quantity = 0;
          }
          return quantity + count;
        }, 0);
      }
    }
  }

  refreshModel(data) {
    this.setModel(data);
    this.orderContact = this.getOrderContact();
    this.setTransactingMember(null);
    this.setTransactingMembershipCategory();
  }

  resetModel() {
    this.model = {
      items: [],
      id: null,
      couponCodes: null,
      membershipIds: null,
      membershipCategoryId: null,
      reservationId: null,
      verifyEntitlements: true,
    };

    if (this.initted) {
      this.setModel(this.model);
      this.coupons.resetModel();
      this.memberships.resetModel();
      this.events.resetModel();
      this.orderContact = this.getOrderContact();
      this.updateOrganization(null);
      this.resetTransactingMember();
      this.memberships.clearCustomerData();
    }
  }

  processMembership(membership) {
    if (membership.membershipExpirationDate) {
      membership.membershipExpirationDateDisplay = this.$filter('date')(
        this.CalendarService.parseDate(membership.membershipExpirationDate),
        this.UserPreferencesService.getDateFormat('shortDate', 'dateFormat')
      );
    }

    return membership;
  }

  getMember(id) {
    const q = this.$q.defer();

    this.ApiService.membership.cardholders.detail(id).then(
      (response) => {
        q.resolve(response);
      },
      (response) => {
        q.reject(response);
      }
    );

    return q.promise;
  }

  getMembership(id) {
    const q = this.$q.defer();

    this.ApiService.membership.detail(id).then(
      (response) => {
        q.resolve(response);
      },
      (response) => {
        q.reject(response);
      }
    );

    return q.promise;
  }

  getMembershipSuccess(response) {
    if (this.transactingMember == null) {
      this.transactingMember = {};
    }

    this.transactingMember.membership = this.processMembership(response.data.payload);
  }

  getMembershipError(response) {
    if (this.transactingMember == null) {
      this.transactingMember = {};
    }

    this.transactingMember.memberships = [];
  }

  getTransactingMemberId() {
    let result = null;

    if (this.transactingMember && this.transactingMember.member) {
      result = this.transactingMember.member.id;
    }

    return result;
  }

  //this could return an array later when we support base + aux memberships
  getTransactingMembershipId() {
    let result = null;
    const allowTicketTransactionOnExpiredMemberships = this.ThemesHelperService.getCompany(
      'allowTicketTransactionOnExpiredMemberships'
    );
    let isExpiredMembership;

    if (this.transactingMember && this.transactingMember.member) {
      isExpiredMembership = this.memberships.isMembershipExpired(this.transactingMember.member);

      if (!isExpiredMembership || allowTicketTransactionOnExpiredMemberships) {
        result = this.transactingMember.member.membershipId;
      }
    }

    return result;
  }

  hasTransactingMembershipCategory(categoryId) {
    const transactingMembershipCategory = this.getTransactingMembershipCategory();
    return transactingMembershipCategory && transactingMembershipCategory === categoryId;
  }

  getTransactingMembershipCategory() {
    return this.transactingMember ? this.transactingMember.membershipCategoryId : null;
  }

  setTransactingMembershipCategory() {
    const membershipLevelIds = this.memberships.getDiscountMembershipLevelIds(true),
      membershipLevelId = membershipLevelIds.length ? membershipLevelIds[0] : null;

    if (this.transactingMember) {
      this.transactingMember.membershipCategoryId = membershipLevelId;
    } else {
      //for new memberships there might not be an existing member.
      this.transactingMember = {
        membershipCategoryId: membershipLevelId,
      };
    }
  }

  getTransactingMembershipNumber() {
    let result = null;

    if (this.transactingMember && this.transactingMember.membership) {
      result = this.transactingMember.membership.acmeMembershipNumber;
    }

    return result;
  }

  getTransactingMembership() {
    return this.transactingMember && this.transactingMember.membership;
  }

  getTransactingMember() {
    return this.transactingMember && this.transactingMember.member;
  }

  setTransactingMember(member) {
    //get the cardholder and membership if it exists and
    // set the member and memberships that belong to this customer.
    const contact = this.getOrderContact();
    const cardId = contact ? contact.cardId : null;
    const updateTransactingMember = (member) => {
      if (!this.transactingMember) {
        this.transactingMember = {};
      }
      this.transactingMember.member = member;

      this.getMembership(member.membershipId).then(
        (response) => {
          this.getMembershipSuccess(response);
        },
        (response) => {
          this.getMembershipError(response);
        }
      );
    };

    if (cardId && this.getTransactingMemberId() !== cardId) {
      if (member) {
        updateTransactingMember(member);
      } else {
        this.getMember(cardId).then(
          (response: any) => {
            const member = response.data.payload;

            updateTransactingMember(member);
          },
          () => {
            //cardholder was not found. reset transacting member.
            this.resetTransactingMember();
          }
        );
      }
    }

    if (!cardId) {
      this.resetTransactingMember();
    }
  }

  resetTransactingMember() {
    this.transactingMember = null;
    this.model.membershipIds = null;
  }

  setItems(items, resetCoupons) {
    this.model.items = items;
    if (resetCoupons) {
      this.coupons.resetModel();
    }
  }

  getItems() {
    return this.model.items || [];
  }

  getItem(itemId) {
    const items = this.getItems().filter((item) => {
      return item.itemId === itemId;
    });

    return items.length ? items[0] : null;
  }

  //TODO: Check if item has an id and try to update instead
  addItem(item) {
    if (!(this.model.items instanceof Array)) {
      this.model.items = [];
    }
    this.model.items.push(item);

    this.itemCount = this.model.items.reduce((count, item) => {
      let quantity = parseInt(item.quantity, 10);
      if (isNaN(quantity)) {
        quantity = 0;
      }
      return quantity + count;
    }, 0);
  }

  updateItem(item) {
    const items = this.getItems();

    for (let i = 0, ii = items.length; i < ii; i++) {
      if (item.itemId === items[i].itemId) {
        items[i] = item;
        break;
      }
    }

    this.itemCount = this.model.items.reduce((count, item) => {
      let quantity = parseInt(item.quantity, 10);
      if (isNaN(quantity)) {
        quantity = 0;
      }
      return quantity + count;
    }, 0);
  }

  removeItem(itemId, skipGetCart = false) {
    let index = -1;
    const items = this.getItems();
    const q = this.$q.defer();

    for (let i = 0, ii = items.length; i < ii; i++) {
      if (itemId === items[i].itemId) {
        index = i;
        break;
      }
    }

    if (index > -1) {
      this.model.items.splice(index, 1);
    }

    this.memberships.setModel(
      this.$filter('filter')(this.model.items, (item) => {
        return item.itemType.toLowerCase().indexOf('membership') > -1;
      })
    );

    this.events.setModel(
      this.$filter('filter')(this.model.items, (item) => {
        return (
          item.itemType.toLowerCase().indexOf('event') > -1 || item.itemType.toLowerCase().indexOf('inventory') > -1
        );
      })
    );

    if (!skipGetCart) {
      this.updateCart().then(
        (response) => {
          q.resolve(response);
        },
        (response) => {
          q.reject(response);
        }
      );
    } else {
      q.resolve();
      this.itemCount = this.model.items.reduce((count, item) => {
        let quantity = parseInt(item.quantity, 10);
        if (isNaN(quantity)) {
          quantity = 0;
        }
        return quantity + count;
      }, 0);
    }

    return q.promise;
  }

  clearMembershipDiscountedEvents() {
    const fromMembership = true;
    const promises = this.events.getEvents().map((event) => this.events.removeDiscountedTickets(event, fromMembership));

    return this.$q.all(promises);
  }

  removeItems(itemIds) {
    let index = -1;
    const remove = (itemId) => {
      const items = this.getItems();

      for (let i = 0, ii = items.length; i < ii; i++) {
        if (itemId === items[i].itemId) {
          index = i;
          break;
        }
      }

      if (index > -1) {
        this.model.items.splice(index, 1);
      }
    };

    angular.forEach(itemIds, (itemId) => {
      remove(itemId);
    });
  }

  getItemByType(itemType) {
    const items = this.getItems().filter((item) => {
      return item.itemType === itemType;
    });

    return items.length ? items[0] : null;
  }

  getMembershipItem() {
    const items = this.getItems().filter((item) => {
      return item.itemType.toLowerCase().indexOf('membership') > -1;
    });

    return items.length ? items[0] : null;
  }

  getEventItemById(eventItem) {
    const eventItemId = eventItem.itemId;
    const eventItemIsRetail = eventItem.isRetail || false;

    const items = this.getItems().filter((item) => {
      const isValidItemType =
          item.itemType.toLowerCase().indexOf('event') > -1 || item.itemType.toLowerCase().indexOf('inventory') > -1,
        isRetail = item.isRetail || false;
      return isValidItemType && item.itemId === eventItemId && isRetail === eventItemIsRetail;
    });

    return items.length ? items[0] : null;
  }

  getItemRetailSubtotal(item) {
    return this.HelpersService.integerToCurrencyFloat(
      item.quantity * this.HelpersService.currencyFloatToInteger(item.retailUnitPrice)
    );
  }

  getItemDiscountedSubtotal(item) {
    return this.HelpersService.integerToCurrencyFloat(
      item.quantity * this.HelpersService.currencyFloatToInteger(item.unitPrice)
    );
  }

  isOrderContact(id) {
    return id && this.model.orderContact && this.model.orderContact.customerId == id;
  }

  hasOrderContact() {
    return this.model.orderContact && this.model.orderContact.customerId;
  }

  setOrderContact(membership) {
    const membershipItem = membership || this.getMembershipItem();

    if (membershipItem) {
      this.model.orderContact = this.memberships.getOrderContact(
        membershipItem.membershipInfo.membershipCards || [],
        membershipItem.membershipInfo.orderContactId
      );
    }
  }

  /*
   * TODO: When we implement event tickets for organisations using the cart,
   * we need to enhance this to also check for organisation order contact
   */
  updateOrderContact(membership?: any) {
    const membershipItem = membership || this.getMembershipItem();

    if (membershipItem) {
      this.model.orderContact = this.memberships.getOrderContact(
        membershipItem.membershipInfo.membershipCards || [],
        membershipItem.membershipInfo.orderContactId
      );

      if (this.model.orderContact) {
        this.model.orderContact.cardId = this.model.orderContact.id;

        if (this.model.orderContact.membershipId) {
          this.setTransactingMember(this.model.orderContact);
        }
      } else {
        this.model.membershipIds = null;
      }
    } else {
      this.model.orderContact = null;
      this.model.membershipIds = null;
    }
  }

  updateOrganization(organization?: any) {
    this.organization = organization;
  }

  getOrganization() {
    return this.organization;
  }

  setReservation(reservationId) {
    if (this.model) {
      this.model.reservationId = reservationId;
    }
  }

  getReservationId() {
    return this.model && this.model.reservationId ? this.model.reservationId : null;
  }

  isMemberCart() {
    return this.getTransactingMembershipId() || this.getTransactingMembershipCategory();
  }

  /**
   * Gets or creates a cart
   */
  getCart() {
    const q = this.$q.defer();

    this.ApiService.cart.detail().then(
      (response) => {
        this.refreshModel(response.data.payload);
        this.initted = true;
        q.resolve(this.getModel());
      },
      (response) => {
        this.createCart().then(
          (response) => {
            this.resetModel();
            this.initted = true;
            q.resolve(response);
          },
          (response) => {
            q.reject(response);
          }
        );
      }
    );

    return q.promise;
  }

  /**
   * Creates a cart
   * @param cart {optional}
   */
  createCart(cart?: any) {
    const q = this.$q.defer();

    this.ApiService.cart.create(cart || { verifyEntitlements: true }).then(
      (response) => {
        this.model.id = response.data.payload;
        q.resolve(this.getModel());
      },
      (response) => {
        q.reject(response);
      }
    );

    return q.promise;
  }

  /**
   * Updates cart or creates a new cart if there is a failure
   */
  updateCart() {
    return Promise.resolve()
      .then(() => {
        return this.ApiService.cart
          .update(this.getSaveModel())
          .then((response) => {
            this.refreshModel(response.data.payload);
            return response;
          })
          .catch((response) => {
            if (response.status === 404) {
              return this.createCart(this.getSaveModel());
            }

            throw response;
          });
      })
      .then((cartResponse) => {
        return this.runCouponValidationsAndReturnCartResponse(cartResponse);
      });
  }

  runCouponValidationsAndReturnCartResponse(cartResponse) {
    const couponCodes = this.HelpersService.path(cartResponse, ['data', 'payload', 'couponCodes'], []);
    const couponsModel = couponCodes.map((couponOrCode) => {
      return typeof couponOrCode === 'string' ? { code: couponOrCode } : couponOrCode.code;
    });

    this.coupons.setModel(couponsModel);

    return this.coupons
      .validate()
      .then(() => {
        return cartResponse;
      })
      .catch(() => {
        return cartResponse;
      });
  }

  /**
   * Clears all items in cart and
   * creates a new one for future use
   */
  cancelCart() {
    const q = this.$q.defer();
    const cart = this.getModel();

    if (cart && cart.id) {
      this.ApiService.cart.delete(cart.id).then(
        (response) => {
          this.resetModel();
          this.createCart().then((response) => {
            q.resolve(response);
          });
        },
        (response) => {
          q.reject(response);
        }
      );
    }

    return q.promise;
  }

  resetCart(skipReInit = false) {
    this.resetModel();
    this.initted = false;
    if (!skipReInit) {
      this.init();
    }
  }

  /**
   * Adds cart item
   */
  addCartItem(item) {
    const cart = this.getModel();
    const membershipId = this.getTransactingMembershipId();
    const q = this.$q.defer();

    if (cart && cart.id) {
      this.ApiService.cart.item.add(cart.id, angular.extend(item, { membershipId: membershipId })).then(
        (response) => {
          q.resolve(response);
        },
        (response) => {
          q.reject(response);
        }
      );
    }

    return q.promise;
  }

  /**
   * Updates cart item
   */
  updateCartItem(item) {
    const cart = this.getModel();
    const membershipId = cart.membershipIds && cart.membershipIds.length ? cart.membershipIds[0] : null;
    const q = this.$q.defer();

    if (cart && cart.id) {
      this.ApiService.cart.item.update(cart.id, angular.extend(item, { membershipId: membershipId })).then(
        (response) => {
          q.resolve(response);
        },
        (response) => {
          q.reject(response);
        }
      );
    }

    return q.promise;
  }

  getTotal() {
    let total = 0;

    angular.forEach(this.memberships.getModel(), (membership) => {
      total += this.HelpersService.currencyFloatToInteger(membership.unitPrice, 2);
    });

    angular.forEach(this.events.getEvents(), (event) => {
      total += this.getTicketAmount(event, false);
      total += this.getAddOnAmount(event, false);
    });

    return this.HelpersService.integerToCurrencyFloat(total, 2);
  }

  getTicketAmount(event, retail = false) {
    let result = 0;
    if (event.tickets instanceof Array) {
      angular.forEach(event.tickets, (ticket) => {
        if (ticket.quantity) {
          if (retail) {
            result += ticket.quantity * this.HelpersService.currencyFloatToInteger(ticket.retailUnitPrice, 2);
          } else {
            result += ticket.quantity * this.HelpersService.currencyFloatToInteger(ticket.unitPrice, 2);
          }
        }
      });
    }

    return result;
  }

  getAddOnAmount(event, retail = false) {
    let result = 0;
    if (event.addOns instanceof Array) {
      angular.forEach(event.addOns, (addOn) => {
        if (addOn.quantity) {
          if (retail) {
            result += addOn.quantity * this.HelpersService.currencyFloatToInteger(addOn.unitPrice, 2);
          } else {
            result += addOn.quantity * this.HelpersService.currencyFloatToInteger(addOn.unitPrice, 2);
          }
        }
      });
    }

    return result;
  }

  getRetailTotal() {
    let retailTotal = 0;

    angular.forEach(this.memberships.getModel(), (membership) => {
      retailTotal += this.HelpersService.currencyFloatToInteger(membership.retailUnitPrice, 2);
    });

    angular.forEach(this.events.getEvents(), (event) => {
      retailTotal += this.getTicketAmount(event, true);
      retailTotal += this.getAddOnAmount(event, true);
    });

    return this.HelpersService.integerToCurrencyFloat(retailTotal, 2);
  }

  getSavings() {
    let retailTotal = 0;
    let total = 0;

    angular.forEach(this.memberships.getModel(), (item) => {
      retailTotal += this.HelpersService.currencyFloatToInteger(item.retailUnitPrice, 2);
      total += this.HelpersService.currencyFloatToInteger(item.unitPrice, 2);
    });

    angular.forEach(this.events.getEvents(), (event) => {
      retailTotal += this.getTicketAmount(event, true);
      retailTotal += this.getAddOnAmount(event, true);
      total += this.getTicketAmount(event);
      total += this.getAddOnAmount(event);
    });

    const savings = retailTotal - total;

    return this.HelpersService.integerToCurrencyFloat(savings, 2);
  }

  setVerifyEntitlements(ignoreLimits) {
    this.model.verifyEntitlements = ignoreLimits ? false : true;
  }

  send(
    event:
      | { type: 'RESET' }
      | { type: 'STATUS_CHANGED'; params: Pick<CartState, 'status'> }
      | { type: 'AUTORENEW_IN_CART_CHANGED'; params: Pick<CartState, 'autorenewInCart'> }
  ) {
    switch (event.type) {
      case 'RESET':
        this.memberships.send({ type: 'RESET' });
        this.stateSubject.next(createInitialState());
        break;

      case 'STATUS_CHANGED':
        this.stateSubject.next({
          ...this.stateSubject.value,
          status: event.params.status,
        });
        break;

      case 'AUTORENEW_IN_CART_CHANGED':
        this.stateSubject.next({
          ...this.stateSubject.value,
          autorenewInCart: createAutorenewInCart(event.params.autorenewInCart),
        });
        break;

      default:
        break;
    }
  }

  setStatus(status: CartStatus) {
    this.send({ type: 'STATUS_CHANGED', params: { status } });
  }

  waitFor(targetStatus: CartStatus) {
    return firstValueFrom(this.state$.pipe(first((state) => state.status === targetStatus)));
  }

  setAutorenewInCart(autorenewInCart: AutorenewInCart) {
    this.send({ type: 'AUTORENEW_IN_CART_CHANGED', params: { autorenewInCart } });
  }
}

export const CartServiceModule = angular
  .module('acme.common.service.cart', [
    'acme.common.service.api',
    CartCouponsServiceModule,
    CartMembershipsServiceModule,
    CartEventsServiceModule,
    'acme.common.service.userPreferences',
    'acme.common.service.calendar',
    'acme.common.service.themesHelper',
  ])
  .service('CartService', CartService).name;
